// editorHooks.js - React hooks for editor functionality
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadPreferences,
  savePreferences,
  loadAudioGUID,
  requestFilter,
  updatePreferences,
  setPlaybackTime,
  selectAnnotation,
  selectTag,
  clearSelection,
  clearAudio,
  clearError,
  reset,
} from ".";
import { NOT_TAGGED } from "../../pages/app/utils/constants";

/**
 * Main hook for editor functionality
 */
export const useEditor = () => {
  const dispatch = useDispatch();
  const editor = useSelector((state) => state.editor);

  // Preferences actions
  const handleLoadPreferences = useCallback(
    (caseId, clipId) => {
      return dispatch(loadPreferences({ caseId, clipId }));
    },
    [dispatch],
  );

  const handleSavePreferences = useCallback(
    (caseId, clipId, preferences) => {
      return dispatch(savePreferences({ caseId, clipId, preferences }));
    },
    [dispatch],
  );

  const handleUpdatePreferences = useCallback(
    (preferences) => {
      return dispatch(updatePreferences(preferences));
    },
    [dispatch],
  );

  // Audio actions
  const handleLoadAudioGUID = useCallback(
    (clipId, commitId, channel) => {
      return dispatch(loadAudioGUID({ clipId, commitId, channel }));
    },
    [dispatch],
  );

  const handleClearAudio = useCallback(() => {
    return dispatch(clearAudio());
  }, [dispatch]);

  // Playback actions
  const handleSetPlaybackTime = useCallback(
    (clipTime, clip) => {
      return dispatch(setPlaybackTime({ clipTime, clip }));
    },
    [dispatch],
  );

  // Selection actions
  const handleSelectAnnotation = useCallback(
    (annotationId, tag) => {
      return dispatch(selectAnnotation({ annotationId, tag }));
    },
    [dispatch],
  );

  const handleSelectTag = useCallback(
    (tag) => {
      return dispatch(selectTag(tag));
    },
    [dispatch],
  );

  const handleClearSelection = useCallback(() => {
    return dispatch(clearSelection());
  }, [dispatch]);

  // Filter/operation actions
  const handleRequestFilter = useCallback(
    (caseId, clipId, filterType, annotations, comment) => {
      return dispatch(
        requestFilter({ caseId, clipId, filterType, annotations, comment }),
      );
    },
    [dispatch],
  );

  // Utility actions
  const handleClearError = useCallback(() => {
    return dispatch(clearError());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    return dispatch(reset());
  }, [dispatch]);

  // Computed values
  const hasSelection = useMemo(
    () =>
      !!(editor.selection.selectedAnnotation || editor.selection.selectedTag),
    [editor.selection.selectedAnnotation, editor.selection.selectedTag],
  );

  const isUntaggedSelected = useMemo(
    () => !!(hasSelection && editor.selection.selectedTag === NOT_TAGGED),
    [hasSelection, editor.selection.selectedTag],
  );

  const canPerformOperations = useMemo(
    () => hasSelection && !isUntaggedSelected && !editor.operations.isFiltering,
    [hasSelection, isUntaggedSelected, editor.operations.isFiltering],
  );

  return {
    // Audio state
    audio: editor.audio,
    isAudioLoaded: editor.audio.isLoaded,
    isAudioLoading: editor.audio.isLoading,
    audioGUID: editor.audio.guid,

    // Playback state
    playback: editor.playback,
    clipTime: editor.playback.clipTime,
    originalTime: editor.playback.originalTime,

    // Preferences
    preferences: editor.preferences,
    isPreferencesLoading: editor.preferences.isLoading,

    // Selection state
    selection: editor.selection,
    selectedAnnotation: editor.selection.selectedAnnotation,
    selectedTag: editor.selection.selectedTag,
    hasSelection,
    isUntaggedSelected,

    // Operations state
    operations: editor.operations,
    isFiltering: editor.operations.isFiltering,
    isTagging: editor.operations.isTagging,
    canPerformOperations,

    // Constants
    NONE_SELECTED: NOT_TAGGED,

    // Error state
    error: editor.error,
    preferencesError: editor.preferences.error,
    operationsError: editor.operations.error,
    hasError: !!(
      editor.error ||
      editor.preferences.error ||
      editor.operations.error
    ),

    // Actions
    loadPreferences: handleLoadPreferences,
    savePreferences: handleSavePreferences,
    updatePreferences: handleUpdatePreferences,
    loadAudioGUID: handleLoadAudioGUID,
    clearAudio: handleClearAudio,
    setPlaybackTime: handleSetPlaybackTime,
    selectAnnotation: handleSelectAnnotation,
    selectTag: handleSelectTag,
    clearSelection: handleClearSelection,
    requestFilter: handleRequestFilter,
    clearError: handleClearError,
    reset: handleReset,
  };
};

/**
 * Hook for editor preferences management
 */
export const useEditorPreferences = () => {
  const {
    preferences,
    updatePreferences,
    savePreferences,
    loadPreferences,
    isPreferencesLoading,
    preferencesError,
  } = useEditor();

  // Update specific preference values
  const handleUpdateWaveOpacity = useCallback(
    (opacity) => {
      updatePreferences({ waveOpacity: opacity });
    },
    [updatePreferences],
  );

  const handleUpdateZoom = useCallback(
    (zoomAmount) => {
      updatePreferences({ zoomAmount });
    },
    [updatePreferences],
  );

  const handleUpdateFFTSamples = useCallback(
    (fftSamples) => {
      updatePreferences({ fftSamples });
    },
    [updatePreferences],
  );

  const handleUpdateFFTFrequencies = useCallback(
    (fftFrequencies) => {
      updatePreferences({ fftFrequencies });
    },
    [updatePreferences],
  );

  const handleUpdateSelectedChannel = useCallback(
    (selectedChannel) => {
      updatePreferences({ selectedChannel });
    },
    [updatePreferences],
  );

  return {
    preferences,
    isLoading: isPreferencesLoading,
    error: preferencesError,
    selectedChannel: preferences.selectedChannel,

    // Actions
    updateWaveOpacity: handleUpdateWaveOpacity,
    updateZoom: handleUpdateZoom,
    updateFFTSamples: handleUpdateFFTSamples,
    updateFFTFrequencies: handleUpdateFFTFrequencies,
    updateSelectedChannel: handleUpdateSelectedChannel,
    updatePreferences,
    savePreferences,
    loadPreferences,
  };
};

/**
 * Hook for editor operations (filter, merge, cut)
 */
export const useEditorOperations = () => {
  const {
    requestFilter,
    selectedTag,
    canPerformOperations,
    isTagging,
    operationsError,
  } = useEditor();

  // Merge operation (EXTRACT filter)
  const handleMergeRegion = useCallback(
    (caseId, clipId, comment) => {
      return requestFilter(caseId, clipId, "EXTRACT", comment);
    },
    [requestFilter],
  );

  // Cut operation (TRIM filter)
  const handleCutRegion = useCallback(
    (caseId, clipId, comment) => {
      return requestFilter(caseId, clipId, "TRIM", comment);
    },
    [requestFilter],
  );

  // Generic filter operation
  const handleFilter = useCallback(
    (caseId, clipId, filterType, tag, comment) => {
      if (!tag) {
        console.warn("No tag selected for filter operation");
        return Promise.resolve({ success: false, error: "No tag selected" });
      }
      return requestFilter(caseId, clipId, filterType, comment);
    },
    [requestFilter],
  );

  return {
    // State
    selectedTag,
    canPerformOperations,
    isTagging,
    error: operationsError,

    // Operations
    mergeRegion: handleMergeRegion,
    cutRegion: handleCutRegion,
    filter: handleFilter,
    requestFilter,
  };
};

/**
 * Hook for editor selection management
 */
export const useEditorSelection = () => {
  const {
    selection,
    selectAnnotation,
    selectTag,
    clearSelection,
    hasSelection,
  } = useEditor();

  // Handle WaveSurfer region click
  const handleRegionClick = useCallback(
    (regionId, tag) => {
      selectAnnotation(regionId, tag);
    },
    [selectAnnotation],
  );

  // Handle tag button click
  const handleTagClick = useCallback(
    (tag) => {
      selectTag(tag);
    },
    [selectTag],
  );

  // Check if specific annotation is selected
  const isAnnotationSelected = useCallback(
    (annotationId) => {
      return selection.selectedAnnotation === annotationId;
    },
    [selection.selectedAnnotation],
  );

  // Check if specific tag is selected
  const isTagSelected = useCallback(
    (tag) => {
      return selection.selectedTag === tag;
    },
    [selection.selectedTag],
  );

  return {
    selection,
    selectedAnnotation: selection.selectedAnnotation,
    selectedTag: selection.selectedTag,
    hasSelection,

    // Actions
    selectAnnotation,
    selectTag,
    clearSelection,
    handleRegionClick,
    handleTagClick,

    // Utilities
    isAnnotationSelected,
    isTagSelected,
  };
};
